import React from 'react'
import { Button, Flex, Typography } from '@paintscout/ui'
import { clearMasqueradeCompanyId } from 'shared/graphql/masquerade'

/**
 * Shown when the company this tab is masquerading into can't be loaded — deleted, renamed, purged, or
 * a bad id in the URL.
 *
 * Every request in that session fails identically at context creation, so there is no `appDocs` and
 * nothing renders: masquerading staff used to be stuck on the loading spinner indefinitely, because
 * the app deliberately doesn't log privileged users out on a failed `appDocs` and the "Stop
 * Masquerading" control lives in a header that never mounts. This is the way out — it clears the
 * masquerade for this tab only and reloads into the user's own account.
 */
export default function MasqueradeUnavailable({ companyId }: { companyId?: string | null }) {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      style={{ height: '75vh', width: '100vw', textAlign: 'center', padding: 24, gap: 12 }}
    >
      <Typography variant="h2" gutterBottom>
        This account could not be loaded
      </Typography>
      <Typography variant="body1" style={{ maxWidth: 460 }}>
        {companyId ? (
          <>
            Nothing could be loaded for <strong>{companyId}</strong>. The account may have been renamed or deleted, or
            the link may be out of date.
          </>
        ) : (
          <>The account you are viewing may have been renamed or deleted, or the link may be out of date.</>
        )}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          clearMasqueradeCompanyId()
          // full reload rather than a route change: the Apollo client is bound to the masqueraded
          // company, so it has to be rebuilt before anything can load
          window.location.assign('/')
        }}
      >
        Stop masquerading
      </Button>
    </Flex>
  )
}
